import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  LocalStorageBackend,
  RedirectRequestHandler,
  TokenRequest,
  TokenResponse
} from '@ariyana/appauth';
import {
  setDoBootstrap,
  setProvideAuthorizationConfig
} from 'redux/slices/systemSlice';
import { getPostLoginCallbackRoute } from 'spec';
import { parseAccessToken } from '../utils/Utils';
import { ApplicationPaths } from './ApiAuthorizationConstants';
import NoHashQueryStringUtils from './noHashQueryStringUtils';

const config = window.client;

const LOCAL_STORAGE_TOKEN_RESPONSE_JSON_KEY = 'tkrs';
const LOCAL_STORAGE_USER_INFO_KEY = 'profile';
const LOCAL_STORAGE_ARY_AUTH_KEY = 'aryauth';

export class AuthorizeService {
  tokenResponseJson;

  configuration;

  userInfo;

  constructor() {
    this.requestor = new FetchRequestor();
    this.tokenHandler = new BaseTokenRequestHandler(this.requestor);

    this.redirectRequestHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto()
    );

    this.localStorageBackend = new LocalStorageBackend();
  }

  async setItemToStorage(key, value) {
    const itemsJson = await this.localStorageBackend.getItem(
      LOCAL_STORAGE_ARY_AUTH_KEY
    );
    const items = itemsJson ? JSON.parse(itemsJson) : {};
    const newItems = { ...items, [key]: value };
    await this.localStorageBackend.setItem(
      LOCAL_STORAGE_ARY_AUTH_KEY,
      JSON.stringify(newItems)
    );
  }

  async getItemFromStorage(key) {
    const itemsJson = await this.localStorageBackend.getItem(
      LOCAL_STORAGE_ARY_AUTH_KEY
    );
    if (!itemsJson) {
      return null;
    }
    const items = JSON.parse(itemsJson);
    return items[key];
  }

  async fetchServiceConfiguration() {
    try {
      const resp = await AuthorizationServiceConfiguration.fetchFromIssuer(
        config.authority,
        this.requestor
      );
      this.configuration = resp;
      return resp;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getServiceConfiguration() {
    let { configuration } = this;
    if (!configuration) {
      configuration = await this.fetchServiceConfiguration();
    }
    return configuration;
  }

  async isAuthenticated() {
    const accessToken = await this.getAccessToken();
    return !!accessToken;
  }

  // ************************* get user info ******************************

  async fetchUserInfo() {
    const configuration = await this.getServiceConfiguration();
    const token = await this.getAccessToken();
    if (!configuration?.userInfoEndpoint || !token) {
      return null;
    }
    const res = await fetch(configuration.userInfoEndpoint, {
      headers: {
        authorization: `Bearer ${token}`
      }
    });

    if (res.status !== 200) {
      return null;
    }

    const userInfo = await res.json();
    this.userInfo = userInfo;
    return userInfo;
  }

  async getUserInfo({ forceRefresh = false } = {}) {
    const userClaims = await this.getClaims();
    const userSubId = userClaims?.sub;

    if (!forceRefresh && this.userInfo?.sub === userSubId) {
      return this.userInfo;
    }

    if (!forceRefresh) {
      const localStorageUserInfo = await this.getItemFromStorage(
        LOCAL_STORAGE_USER_INFO_KEY
      );

      const storedSubId = localStorageUserInfo?.sub;

      if (userSubId && userSubId === storedSubId) {
        this.userInfo = localStorageUserInfo;
        return localStorageUserInfo;
      }
    }

    const resp = await this.fetchUserInfo();

    this.userInfo = resp;

    if (resp) {
      await this.setItemToStorage(LOCAL_STORAGE_USER_INFO_KEY, resp);
    }

    return resp;
  }

  async getFullName() {
    const user = await this.getUserInfo();
    return user?.fullname;
  }

  async getUserName() {
    const user = await this.getUserInfo();
    return user?.name;
  }

  async getManagerId() {
    const user = await this.getUserInfo();
    return user?.aid;
  }

  // *********************** end get user info ****************************
  // ****************************** sign in *******************************

  async makeAuthorizationRequest() {
    const LOCK_KEY = 'login_lock';

    const rawLock = sessionStorage.getItem(LOCK_KEY);

    if (rawLock) {
      try {
        const lock = JSON.parse(rawLock);

        const isExpired = Date.now() - lock.createdAt > 10000;

        if (!isExpired) {
          console.warn('Login already in progress');

          return;
        }
        sessionStorage.removeItem(LOCK_KEY);
      } catch {
        sessionStorage.removeItem(LOCK_KEY);
      }
    }

    sessionStorage.setItem(
      LOCK_KEY,
      JSON.stringify({
        createdAt: Date.now()
      })
    );

    try {
      const configuration = await this.getServiceConfiguration();
      const request = new AuthorizationRequest({
        client_id: config.client_id,
        redirect_uri: config.redirect_uri,
        scope: config.scope,
        response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
        state: crypto.randomUUID()
      });
      sessionStorage.setItem(
        'originalPath',
        `${window.location.pathname}${window.location.search}`
      );
      this.redirectRequestHandler.performAuthorizationRequest(
        configuration,
        request
      );
    } catch (error) {
      sessionStorage.removeItem(LOCK_KEY);
      console.error(error);
    }
  }

  // ****************************** end sign in *******************************
  // ****************************** get refresh token *******************************

  async fetchRefreshToken(navigate, dispatchToReduxStore) {
    const redirectRequestHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto()
    );

    const notifier = new AuthorizationNotifier();

    redirectRequestHandler.setAuthorizationNotifier(notifier);

    notifier.setAuthorizationListener(async (request, response, error) => {
      if (error) {
        sessionStorage.removeItem('login_lock');
        console.error(error);
        return;
      }
      if (!request || !response) {
        sessionStorage.removeItem('login_lock');
        return;
      }
      try {
        this.userInfo = null;
        this.tokenResponseJson = null;

        const extras = {
          code_verifier: request.internal.code_verifier
        };

        const tokenRequest = new TokenRequest({
          client_id: config.client_id,
          redirect_uri: config.redirect_uri,
          grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
          code: response.code,
          extras
        });

        const configuration = await this.getServiceConfiguration();

        const tokenResponse = await this.tokenHandler.performTokenRequest(
          configuration,
          tokenRequest
        );

        const tokenResponseJson = tokenResponse.toJson();

        await this.setItemToStorage(
          LOCAL_STORAGE_TOKEN_RESPONSE_JSON_KEY,
          tokenResponseJson
        );

        this.tokenResponseJson = tokenResponseJson;

        /*
          Important:
          Fetch the new user's profile before bootstrap and navigate.
          This prevents showing previous user's profile for a short moment.
        */
        await this.getUserInfo({ forceRefresh: true });

        dispatchToReduxStore(setProvideAuthorizationConfig(true));
        dispatchToReduxStore(setDoBootstrap(true));

        navigate(getPostLoginCallbackRoute(tokenResponse.accessToken));
      } catch (e) {
        console.error('fetchRefreshToken failed:', e);
        this.makeAuthorizationRequest();
      } finally {
        sessionStorage.removeItem('login_lock');
      }
    });

    await redirectRequestHandler.completeAuthorizationRequestIfPossible();
  }

  async getRefreshToken() {
    const { tokenResponseJson } = this;
    let tokenResponse;

    if (tokenResponseJson) {
      tokenResponse = new TokenResponse(tokenResponseJson);
    }

    if (!tokenResponse) {
      const localStorageTokenResponseJson = await this.getItemFromStorage(
        LOCAL_STORAGE_TOKEN_RESPONSE_JSON_KEY
      );
      if (localStorageTokenResponseJson) {
        const localStorageTokenResponse = new TokenResponse(
          localStorageTokenResponseJson
        );
        this.tokenResponseJson = localStorageTokenResponseJson;
        tokenResponse = localStorageTokenResponse;
      } else {
        return null;
      }
    }
    return tokenResponse ? tokenResponse.refreshToken : null;
  }

  // ****************************** end get refresh token *******************************
  // ****************************** get access token *******************************

  async fetchTokenResponseJson() {
    const configuration = await this.getServiceConfiguration();
    const refreshToken = await this.getRefreshToken();
    if (!refreshToken) {
      if (window.location.pathname !== ApplicationPaths.LoginCallback) {
        this.makeAuthorizationRequest();
      }
      return null;
    }
    const request = new TokenRequest({
      client_id: config.client_id,
      redirect_uri: config.redirect_uri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      code: undefined,
      refresh_token: refreshToken,
      extras: undefined
    });

    try {
      const resp = await this.tokenHandler.performTokenRequest(
        configuration,
        request
      );

      const tokenResponseJson = resp.toJson();
      await this.setItemToStorage(
        LOCAL_STORAGE_TOKEN_RESPONSE_JSON_KEY,
        tokenResponseJson
      );
      this.tokenResponseJson = tokenResponseJson;
      return tokenResponseJson;
    } catch {
      this.makeAuthorizationRequest();
      return undefined;
    }
  }

  async getTokenResponseJson() {
    const isAccessTokenValid = (tokenResponseJson) => {
      if (!tokenResponseJson) return false;
      const tokenResponse = new TokenResponse(tokenResponseJson);
      return !!tokenResponse.accessToken && tokenResponse.isValid();
    };
    let { tokenResponseJson } = this;
    if (!isAccessTokenValid(tokenResponseJson)) {
      const localStorageTokenResponseJson = await this.getItemFromStorage(
        LOCAL_STORAGE_TOKEN_RESPONSE_JSON_KEY
      );
      if (isAccessTokenValid(localStorageTokenResponseJson)) {
        tokenResponseJson = localStorageTokenResponseJson;
        this.tokenResponseJson = tokenResponseJson;
      } else {
        tokenResponseJson = await this.fetchTokenResponseJson();
      }
    }

    if (!tokenResponseJson) {
      return null;
    }

    return tokenResponseJson;
  }

  async getAccessToken() {
    const tokenResponseJson = await this.getTokenResponseJson();
    let tokenResponse;
    if (tokenResponseJson) {
      tokenResponse = new TokenResponse(tokenResponseJson);
    }
    return tokenResponse ? tokenResponse.accessToken : null;
  }

  async getIdToken() {
    const tokenResponseJson = await this.getTokenResponseJson();
    let tokenResponse;
    if (tokenResponseJson) {
      tokenResponse = new TokenResponse(tokenResponseJson);
    }
    return tokenResponse ? tokenResponse.idToken : null;
  }

  async getVersionExpireTime() {
    const accessToken = await this.getAccessToken();
    return accessToken ? parseAccessToken(accessToken).exa : null;
  }

  async isVersionValid() {
    const exa = await this.getVersionExpireTime();

    if (exa === null) {
      return null;
    }

    return !!(new Date().getTime() < +exa);
  }

  // ****************************** end get access token *******************************
  // ****************************** get claims *******************************

  async getClaims() {
    const token = await this.getAccessToken();
    if (!token) {
      return null;
    }
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return null;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    const parsedToken = JSON.parse(jsonPayload);

    if (parsedToken.getBodyClaims) {
      return JSON.parse(parsedToken.clms);
    }

    return parsedToken;
  }

  async getBodyClaims() {
    const token = await this.getAccessToken();
    if (!token) {
      return null;
    }
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return null;
    }
    return JSON.parse(decodeURIComponent(escape(window.atob(base64Url))));
  }

  async getRole() {
    const token = await this.getAccessToken();

    if (!token) {
      return null;
    }

    const base64Url = token.split('.')[1];

    if (!base64Url) {
      return null;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    const parsedToken = JSON.parse(jsonPayload);

    return parsedToken.role;
  }

  // ****************************** end get claims *******************************
  // ****************************** check session *******************************

  async checkSession() {
    const token = await this.getAccessToken();
    const resp = await fetch(`${config.authority}/api/Validity/Valid`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`
      }
    });
    return resp;
  }

  // ****************************** end check session *******************************
  // ****************************** sign out *******************************

  async signOut(state) {
    try {
      const userStatus = await this.checkSession();
      if (userStatus.status === 401) {
        await this.clearUserInfoCache();
        this.makeAuthorizationRequest();
        return;
      }
      sessionStorage.removeItem('customer_credit_account_number');

      const configuration = await this.getServiceConfiguration();
      const idToken = await this.getIdToken();

      const params = new URLSearchParams();

      if (idToken) {
        params.append('id_token_hint', idToken);
      }
      params.append(
        'post_logout_redirect_uri',
        config.post_logout_redirect_uri
      );

      const returnUrl =
        state?.returnUrl ||
        `${window.location.pathname}${window.location.search}`;

      if (returnUrl) {
        params.append('state', returnUrl);
      }

      const url = `${configuration.endSessionEndpoint}?${params.toString()}`;

      await this.clearUserInfoCache();

      window.location.href = url;
    } catch (e) {
      await this.clearUserInfoCache();

      console.error('signOut failed:', e);

      this.makeAuthorizationRequest();
    }
  }

  async clearUserInfoCache() {
    await this.localStorageBackend.removeItem(LOCAL_STORAGE_ARY_AUTH_KEY);
    this.userInfo = null;
    this.tokenResponseJson = null;
  }

  // ****************************** end sign out *******************************
}

const authService = new AuthorizeService();

export default authService;
